import type { FieldOption } from './opensearch-field-mapper';
import type { ColumnFormatConfig, ColumnFormatMap } from './column-format-options';

export interface ExportColumnParams {
  columnKeys: string[];
  columnLabels: Record<string, string>;
  columnFormats: Record<string, ColumnFormatConfig>;
}

/**
 * Build the column_keys / column_labels / column_formats payload for
 * server-side CSV export (POST /search/export-asset).
 *
 * The export pipeline addresses CSV columns by the OpenSearch source path it
 * reads from each record, while the viewer/builder label and format maps are
 * keyed by FE field key — so both maps are re-keyed by path here, falling
 * back to the field's default label when no override exists.
 */
export function buildExportColumnParams(
  columns: FieldOption[],
  columnLabelMap: Record<string, string> = {},
  columnFormatMap: ColumnFormatMap = {}
): ExportColumnParams {
  const columnKeys: string[] = [];
  const columnLabels: Record<string, string> = {};
  const columnFormats: Record<string, ColumnFormatConfig> = {};

  for (const col of columns) {
    const path = col.path || col.key;
    if (!path) continue;
    columnKeys.push(path);
    const label = columnLabelMap[col.key] || col.label;
    if (label) columnLabels[path] = label;
    const format = columnFormatMap[col.key];
    if (format) columnFormats[path] = format;
  }

  return { columnKeys, columnLabels, columnFormats };
}
